import { useRef, useState, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import clsx from 'clsx';
import type { UploadFile } from 'antd';
import type { FileUploadProps } from './types';
import { uploadFile } from '@/services/uploadService';
import UploadIcon from './icons/upload.svg?react';
import CloseIcon from './icons/close.svg?react';

// 扩展 UploadFile 类型以支持进度
interface ExtendedUploadFile extends UploadFile {
  progress?: number;
}

// 暴露给父组件的方法
export interface FileUploadRef {
  uploadFiles: (files: File[]) => Promise<void>;
}

export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(({ fileList, onChange }, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 缓存已生成的 blob URL，避免重复创建导致闪烁
  const blobUrlCacheRef = useRef<Map<string, string>>(new Map());

  // 生成文件预览URL（使用缓存避免闪烁）
  const previewUrls = useMemo(() => {
    const cache = blobUrlCacheRef.current;
    const newCache = new Map<string, string>();

    const urls = fileList.map((file) => {
      // 优先检查缓存中是否已有该文件的 blob URL（保持本地预览）
      if (cache.has(file.uid)) {
        const cachedUrl = cache.get(file.uid)!;
        newCache.set(file.uid, cachedUrl);
        return cachedUrl;
      }
      // 生成新的 blob URL 并缓存
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        newCache.set(file.uid, blobUrl);
        return blobUrl;
      }
      // 如果没有本地文件对象，才使用服务器返回的 url（兜底）
      if (file.url) {
        return file.url;
      }
      return '';
    });

    // 清理不再使用的 blob URL
    cache.forEach((url, uid) => {
      if (!newCache.has(uid) && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    // 更新缓存
    blobUrlCacheRef.current = newCache;

    return urls;
  }, [fileList]);

  // 组件卸载时取消所有上传并清理 blob URL
  useEffect(() => {
    return () => {
      // 取消所有进行中的上传
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current.clear();
      // 清理 blob URL
      blobUrlCacheRef.current.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // 验证文件，返回错误信息或 null（不再直接调用 message.error）
  const validateFile = (file: File, currentCount: number): string | null => {
    const isValidType = file.type.startsWith('image/');

    if (!isValidType) {
      return t('feedback.screenshots.invalidTypeError');
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      return t('feedback.screenshots.maxSizeError');
    }

    if (currentCount >= 5) {
      return t('feedback.screenshots.maxFilesError');
    }

    return null;
  };

  // 使用 ref 保存最新的 fileList，避免闭包陷阱
  const fileListRef = useRef(fileList);
  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  // 更新单个文件的状态
  const updateFileStatus = useCallback(
    (uid: string, updates: Partial<ExtendedUploadFile>) => {
      // 使用 ref 获取最新的 fileList
      const currentList = fileListRef.current;
      const updatedList = currentList.map((f) =>
        f.uid === uid ? { ...f, ...updates } : f
      ) as UploadFile[];
      onChange(updatedList);
    },
    [onChange]
  );

  // 存储每个上传任务的 AbortController
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // 上传单个文件
  const uploadSingleFile = useCallback(
    async (file: File, uid: string) => {
      const controller = new AbortController();
      abortControllersRef.current.set(uid, controller);

      await uploadFile({
        file,
        signal: controller.signal,
        onProgress: (info) => {
          updateFileStatus(uid, {
            status: 'uploading',
            percent: info.progress,
          });
        },
        onSuccess: (info) => {
          abortControllersRef.current.delete(uid);
          updateFileStatus(uid, {
            status: 'done',
            percent: 100,
            url: info.url,
          });
        },
        onFailed: (_info, errorCode) => {
          abortControllersRef.current.delete(uid);
          const errorMessage =
            errorCode === 'NETWORK_ERROR'
              ? t('feedback.screenshots.networkError')
              : t('feedback.screenshots.uploadFailed');
          updateFileStatus(uid, {
            status: 'error',
            error: { message: errorMessage },
          });
        },
      });
    },
    [updateFileStatus]
  );

  const handleClick = () => {
    setIsFocused(true);
    inputRef.current?.click();
  };

  // 处理文件并上传（抽取公共逻辑）
  const processAndUploadFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newFiles: ExtendedUploadFile[] = [];
    const filesToUpload: { file: File; uid: string }[] = [];
    const errors: string[] = [];

    // 计算剩余可用位置
    const remainingSlots = 5 - fileList.length;
    const selectedCount = fileArray.length;

    // 如果已达到上限，直接返回并显示错误
    if (remainingSlots <= 0) {
      message.error(t('feedback.screenshots.maxFilesError'));
      return;
    }

    // 如果选择的文件数量超过剩余位置，提示用户（不与maxFilesError重复）
    if (selectedCount > remainingSlots) {
      message.warning(
        t('feedback.screenshots.exceedLimitWarning', {
          selected: selectedCount,
          selectedUnit: selectedCount === 1 ? 'file' : 'files',
          remaining: remainingSlots,
          remainingUnit: remainingSlots === 1 ? 'slot' : 'slots',
        })
      );
    }

    fileArray.forEach((file) => {
      const validationError = validateFile(file, fileList.length + newFiles.length);
      // 跳过"最多5个文件"的错误，因为已在上面处理过了
      if (validationError === t('feedback.screenshots.maxFilesError')) {
        return;
      }
      if (validationError) {
        if (!errors.includes(validationError)) {
          errors.push(validationError);
        }
        return;
      }
      const uid = `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2)}`;
      newFiles.push({
        uid,
        name: file.name,
        status: 'uploading',
        percent: 0,
        originFileObj: file as any,
      });
      filesToUpload.push({ file, uid });
    });

    // 统一显示错误信息（去重后只显示一次）
    if (errors.length > 0) {
      message.error(errors[0]);
    }

    if (newFiles.length === 0) return;

    // 先更新文件列表（显示上传中状态）
    const updatedList = [...fileList, ...newFiles].slice(0, 5);
    onChange(updatedList as UploadFile[]);

    // 并行上传所有文件
    try {
      await Promise.all(
        filesToUpload.map(({ file, uid }) => uploadSingleFile(file, uid))
      );
    } catch (err) {
      console.error('Upload error:', err);
    }
  };

  // 暴露 uploadFiles 方法给父组件（用于粘贴上传）
  useImperativeHandle(ref, () => ({
    uploadFiles: async (files: File[]) => {
      await processAndUploadFiles(files);
    },
  }), [processAndUploadFiles]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    await processAndUploadFiles(files);

    // 清空input以便重复选择同一文件
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFocused(true);

    const files = e.dataTransfer.files;
    if (!files) return;

    await processAndUploadFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemove = (e: React.MouseEvent, uid: string) => {
    e.stopPropagation();
    // 取消正在进行的上传请求
    const controller = abortControllersRef.current.get(uid);
    if (controller) {
      abortControllersRef.current.delete(uid);
      controller.abort();
    }
    onChange(fileList.filter((file) => file.uid !== uid));
  };



  // 渲染文件项
  const renderFileItem = (file: ExtendedUploadFile, index: number) => {
    const isUploading = file.status === 'uploading';
    const isError = file.status === 'error';
    const progress = file.percent ?? 0;

    return (
      <div
        key={file.uid}
        className="group relative h-[80px] w-[80px] overflow-hidden rounded-lg border-[0.6px] border-[#cccccc26] bg-[#2c2c2d]"
      >
        {/* 预览内容 */}
        <img
          src={previewUrls[index]}
          alt={file.name}
          className="h-full w-full object-cover"
        />

        {/* 错误状态蒙层 */}
        {isError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
            <span className="text-[10px] text-[#f14c4c]">{t('feedback.screenshots.uploadFailed')}</span>
          </div>
        )}

        {/* 上传中蒙层 */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/50 transition-opacity" />
        )}

        {/* 正常状态 hover 蒙层 */}
        {!isUploading && !isError && (
          <div className="absolute inset-0 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100" />
        )}

        {/* 右上角：上传中显示进度，hover显示删除按钮 */}
        <div className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center">
          {/* 上传进度 - 上传中且未hover时显示（无背景） */}
          {isUploading && (
            <div className="flex h-4 w-4 items-center justify-center transition-opacity group-hover:opacity-0">
              <svg className="h-4 w-4" viewBox="0 0 36 36">
                <path
                  className="stroke-[#ffffff40]"
                  fill="none"
                  strokeWidth="4"
                  d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="stroke-[#ffffff]"
                  fill="none"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${progress}, 100`}
                  d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
            </div>
          )}
          {/* 删除按钮 - hover时显示（上传中hover也显示） */}
          <button
            type="button"
            onClick={(e) => handleRemove(e, file.uid)}
            className={clsx(
              'absolute flex h-4 w-4 items-center justify-center rounded bg-[#454545] transition-opacity',
              'opacity-0 group-hover:opacity-100'
            )}
          >
            <CloseIcon className="h-2.5 w-2.5 text-white" />
          </button>
        </div>
      </div>
    );
  };

  // 如果有文件，显示文件列表和上传按钮
  if (fileList.length > 0) {
    return (
      <div className="flex flex-wrap gap-2">
        {(fileList as ExtendedUploadFile[]).map((file, index) => renderFileItem(file, index))}
        {fileList.length < 5 && (
          <div
            ref={containerRef}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className={clsx(
              'box-border flex h-[80px] w-[80px] cursor-pointer flex-col items-center justify-center gap-1 rounded border border-dashed transition-all',
              isFocused
                ? 'border-[#007acc] bg-[#2c2c2d]'
                : 'border-[#cccccc26] bg-[#2c2c2d] hover:bg-[#252526]'
            )}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/gif"
              onChange={handleFileChange}
              className="hidden"
            />
            <UploadIcon className="h-3 w-3 text-[#c5c5c5]" />
            <span className="font-pingfang text-[10px] leading-[150%] text-[#cccccc80]">
              {t('feedback.screenshots.add')}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className={clsx(
        'box-border flex h-[100px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded p-[7.4px] transition-all',
        isFocused
          ? 'dashed-border-focused bg-[#2c2c2d]'
          : 'dashed-border bg-[#2c2c2d] hover:bg-[#252526]'
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/gif"
        onChange={handleFileChange}
        className="hidden"
      />
      <UploadIcon className="h-3 w-3 text-[#c5c5c5]" />
      <div className="flex flex-col items-center gap-0.5">
        <span className="font-pingfang text-[10px] leading-[150%] text-[#cccccc]">
          {t('feedback.screenshots.upload')}
        </span>
        <span className="whitespace-pre-line text-center font-pingfang text-[10px] leading-[120%] text-[#cccccc80]">
          {t('feedback.screenshots.hint')}
        </span>
      </div>
    </div>
  );
});

FileUpload.displayName = 'FileUpload';
